import {Dropdown, TextArea, TextInput, Toggle} from '@/components/atoms'
import {SectionTitle} from '@/components/molecules'
import ImagePicker from '@/containers/Promotions/components/ImagePicker'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'
import {uploadPromotionMedia} from '@/services/PromotionsService/promotions'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {useEffect} from 'react'
import {useSelector} from 'react-redux'
import './_promotionsDisplayConfig.scss'

const mockLanguages = [
  {code: 'en', language: 'English'},
  {code: 'es-MX', language: 'Spanish (Mexico)'},
]

const PromotionsDisplayConfig = () => {
  const {promotion, onResultChange, updatePromotion, promotionConfig} = usePromotion()
  const languagesFromHook = useFeatureFlagLanguages()
  const dispatch = useAppDispatch()
  const routeTypeIdSelected = promotion?.routeTypeId
    ? [{id: promotion?.routeTypeId, displayText: promotion?.routeTypeId}]
    : [{id: 'Empty', displayText: 'Empty'}]
  const countryCode = useSelector(selectCountryCode)
  const promotionImageLabel = countryCode == 'IND' ? 'Promotion Image' : 'Promotion Image*'
  const bannerTagLabel = countryCode == 'IND' ? 'Banner Tag' : 'Banner Tag*'

  // Set default bannerType if not already set
  useEffect(() => {
    if (!promotion.bannerType && promotionConfig?.bannerType?.length > 0) {
      const defaultBannerType = promotionConfig.bannerType[0]
      let updateData = {bannerType: defaultBannerType}

      // If default is clickable, also set default landing page
      if (defaultBannerType === 'clickable' && promotionConfig?.landingPage?.length > 0) {
        updateData.landingPage = promotionConfig.landingPage[0].value
      }

      onResultChange(updateData)
    }
  }, [promotion.bannerType, promotionConfig, onResultChange])

  const languages = languagesFromHook && languagesFromHook.length > 0 ? languagesFromHook : mockLanguages

  const handleTextChange = (value, language, field) => {
    onResultChange({
      text: {
        ...promotion.text,
        [language]: {
          ...promotion.text?.[language],
          [field]: value,
        },
      },
    })
  }

  const handleLocationChange = value => {
    // Convert array of objects to array of strings (extract id values)
    const displayOnArray = Array.isArray(value) ? value.map(item => item.id) : []
    onResultChange({displayOn: displayOnArray})
  }

  const handleOrderChange = value => {
    // displayOrder should be a single number, not an array
    const displayOrderValue = Array.isArray(value) && value.length > 0 ? parseInt(value[0].id) : 0
    onResultChange({displayOrder: displayOrderValue})
  }

  const handleDisplayFirstChange = value => {
    updatePromotion(prev => {
      const newData = {
        ...prev,
        displayOrder: value === true ? 1 : undefined,
      }
      return newData
    })
  }

  const displayFirst = promotion.displayOrder === 1

  const handleBannerTypeChange = value => {
    // Extract the single selected value from the array
    const bannerType = Array.isArray(value) && value.length > 0 ? value[0].id : 'static'

    let updateData = {bannerType}

    if (bannerType === 'static') {
      // Reset landing page to empty string when banner type is static
      updateData.landingPage = ''
    }

    onResultChange(updateData)
  }

  const handleRouteTypeIdChange = value => {
    const routeTypeId = value[0]?.id
    onResultChange({routeTypeId: routeTypeId})
  }
  const handleLandingPageChange = value => {
    // Extract the single selected value from the array
    const landingPage = Array.isArray(value) && value.length > 0 ? value[0].id : null
    onResultChange({landingPage})
  }

  const handleBannerUrlChange = async file => {
    if (!file) {
      updatePromotion(prev => ({...prev, bannerUrl: '', bannerFile: null}))
      return
    }

    // create a local preview so ImagePicker receives a new value immediately
    const previewUrl = URL.createObjectURL(file)
    updatePromotion(prev => ({...prev, bannerUrl: previewUrl, bannerFile: file}))

    if (!promotion?.id) {
      console.error('No promotion id is available for uploading media.')
      return
    }

    const metadata = {
      mediaUploadMetadata: [
        {
          id: promotion?.id,
          originalFileName: file.name,
          originalFormat: file.type.split('/')[1], // e.g., 'png'
          originalSize: file.size,
          blobFileSize: file.size,
          blobFormat: file.type.split('/')[1],
          referenceEntity: 'promotion',
        },
      ],
    }

    const formData = new FormData()
    formData.append('files', file)
    formData.append('metadata', JSON.stringify(metadata))
    try {
      const response = await uploadPromotionMedia(promotion.id, formData)
      const bannerUrl = response?.data?.[0]?.imageCDNUrl
      updatePromotion(prev => ({...prev, bannerUrl, bannerFile: null}))
    } catch (error) {
      // Get the specific error message from the response
      let errorMessage = 'Error uploading image'

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.data?.message) {
        errorMessage = error.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }

      // Check if the error is related to filename issues
      if (
        errorMessage.toLowerCase().includes('filename') ||
        errorMessage.toLowerCase().includes('name') ||
        errorMessage.toLowerCase().includes('invalid')
      ) {
        errorMessage =
          'Filename contains special characters that are not supported. Please rename your file to use only letters, numbers, hyphens, and underscores.'
      }

      // dispatch the error to the toast notification and reset the image upload
      dispatch(
        setToastNotifications({
          show: true,
          message: errorMessage,
          type: 'error',
        })
      )
      updatePromotion(prev => ({...prev, bannerUrl: '', bannerFile: null}))
    }
  }

  const displayOptions = [
    {displayText: '1', id: '1', isBadge: false},
    {displayText: '2', id: '2', isBadge: false},
    {displayText: '3', id: '3', isBadge: false},
    {displayText: '4', id: '4', isBadge: false},
    {displayText: '5', id: '5', isBadge: false},
  ]

  const locationOptions = [
    {displayText: 'Home Page', id: 'HOME_PAGE', isBadge: false},
    {displayText: 'Promotions Main Page', id: 'PROMOTIONS_MAIN_PAGE', isBadge: false},
    {displayText: 'Product Proof', id: 'PRODUCT_PROOF', isBadge: false},
    {displayText: 'Product Card', id: 'PRODUCT_CARD', isBadge: false},
    {displayText: 'Product Catalogue', id: 'PRODUCT_CATALOGUE', isBadge: false},
  ]

  const bannerTypeOptions =
    promotionConfig?.bannerType?.map(type => ({
      displayText: type.charAt(0).toUpperCase() + type.slice(1), // Capitalize first letter
      id: type,
      isBadge: false,
    })) || []

  const routeTypeIdOptions =
    promotionConfig?.routeTypeId?.map(type => ({
      displayText: type,
      id: type,
      isBadge: false,
    })) || []
  const landingPageOptions =
    promotionConfig?.landingPage?.map(page => ({
      displayText: page.label,
      id: page.value,
      isBadge: false,
    })) || []

  // Helper function to convert displayOn array to dropdown format
  const getDisplayOnDropdownValue = () => {
    if (!promotion.displayOn || !Array.isArray(promotion.displayOn)) return []

    return promotion.displayOn.map(id => {
      const option = locationOptions.find(opt => opt.id === id)
      return option ? {id, displayText: option.displayText} : {id, displayText: id}
    })
  }

  // Helper function to convert displayOrder to dropdown format
  const getDisplayOrderDropdownValue = () => {
    if (!promotion.displayOrder) return []

    const displayOrderStr = promotion.displayOrder.toString()
    return [{id: displayOrderStr, displayText: displayOrderStr}]
  }

  const getBannerTypeDropdownValue = () => {
    if (!promotion.bannerType) {
      // Use first option from promotionConfig if available
      const firstOption = promotionConfig?.bannerType?.[0]
      if (firstOption) {
        return [{id: firstOption, displayText: firstOption.charAt(0).toUpperCase() + firstOption.slice(1)}]
      }
      return [{id: 'static', displayText: 'Static'}] // Fallback
    }
    return [
      {
        id: promotion.bannerType,
        displayText: promotion.bannerType?.charAt(0).toUpperCase() + promotion.bannerType?.slice(1),
      },
    ]
  }

  const getLandingPageDropdownValue = () => {
    if (!promotion.landingPage) {
      return [{id: 'Empty', displayText: 'Empty'}]
    }
    const option = promotionConfig?.landingPage?.find(page => page.value === promotion.landingPage)
    return option ? [{id: option.value, displayText: option.label}] : []
  }

  const showsOnPlaceholder = promotion?.displayOn
    ?.map(id => {
      const option = locationOptions.find(opt => opt.id === id)
      return option ? option.displayText : ''
    })
    .join(', ')

  return (
    <div className="mt-4 rounded-lg bg-backgroundAlt p-4">
      {/* <SectionTitle label="Get 10% off when buying any 5 Cheetos or Daritos" /> */}
      <div className="border-t border-t-[#3A3A3A]"></div>

      <h3 className="my-4 font-medium">Promotion Banner </h3>
      <ImagePicker onImageSelect={handleBannerUrlChange} value={promotion?.bannerUrl} title={promotionImageLabel} />
      <div className="my-4 flex flex-row space-x-10">
        <Dropdown
          className="dropdown"
          isJsonWrap={false}
          childList={locationOptions}
          isDark={true}
          label="Promotion Shows On"
          placeholder={showsOnPlaceholder || 'Select Option'}
          selection="multiple"
          size="medium"
          style={{maxWidth: '425px'}}
          value={getDisplayOnDropdownValue()}
          updateValue={handleLocationChange}
          showSelectedCount
          selectedCountColor="green"
          isMultiSelect={true}
          showSelectedItems={true}
        />
        <div className="mt-7">
          <Toggle
            title="Display First"
            size="medium"
            style={{}}
            value={displayFirst}
            variant="horizontal"
            updateValue={handleDisplayFirstChange}
          />
        </div>
        {!displayFirst && (
          <Dropdown
            isJsonWrap={false}
            childList={displayOptions}
            isDark={true}
            className="dropdown"
            label="Display Order"
            placeholder="Display Order"
            selection="single"
            size="medium"
            value={getDisplayOrderDropdownValue()}
            updateValue={handleOrderChange}
            required={true}
          />
        )}
      </div>

      <div className="mb-4 flex flex-row space-x-10">
        <Dropdown
          isJsonWrap={false}
          childList={routeTypeIdOptions}
          isDark={true}
          className="dropdown"
          isClearable={false}
          label="Route Type ID"
          placeholder="Select Banner Type"
          selection="single"
          size="medium"
          value={routeTypeIdSelected}
          updateValue={handleRouteTypeIdChange}
        />
      </div>

      <div className="mb-4 flex flex-row space-x-10">
        <Dropdown
          isJsonWrap={false}
          childList={bannerTypeOptions}
          isDark={true}
          className="dropdown"
          isClearable={false}
          label="Banner Type"
          placeholder="Select Banner Type"
          selection="single"
          size="medium"
          value={getBannerTypeDropdownValue()}
          updateValue={handleBannerTypeChange}
        />
        {promotion.bannerType === 'clickable' && (
          <Dropdown
            isJsonWrap={false}
            childList={landingPageOptions}
            isDark={true}
            isClearable={false}
            className="dropdown"
            label="Landing Page *"
            placeholder="Select Landing Page"
            selection="single"
            size="medium"
            value={getLandingPageDropdownValue()}
            updateValue={handleLandingPageChange}
          />
        )}
      </div>

      <div className="border-t border-t-[#3A3A3A]"></div>
      <SectionTitle label="Promotion Text" />

      <div className="grid grid-cols-3 gap-4">
        {languages.map(lang => {
          const languageName = lang.language
          const language = lang.code

          return (
            <div key={language} className="mt-6 space-y-5 font-semibold">
              <h3 className="font-semibold">{languageName}</h3>
              <TextInput
                feedbackType="info"
                inputSize="medium"
                label="Product Tag*"
                className="promotion-text-input"
                onChange={value => handleTextChange(value, language, 'productTag')}
                onUpdate={value => handleTextChange(value, language, 'productTag')}
                placeholderText="10% Discount"
                value={promotion?.text?.[language]?.productTag || ''}
                autocomplete={language === 'es_MX' ? 'off' : 'on'}
              />
              <TextInput
                feedbackType="info"
                inputSize="medium"
                label={bannerTagLabel}
                className="promotion-text-input"
                onChange={value => handleTextChange(value, language, 'bannerTag')}
                onUpdate={value => handleTextChange(value, language, 'bannerTag')}
                placeholderText="10% Discount"
                value={promotion?.text?.[language]?.bannerTag || ''}
                autocomplete={language === 'es_MX' ? 'off' : 'on'}
              />
              <TextInput
                feedbackType="info"
                inputSize="medium"
                label="Excerpt"
                className="promotion-text-input"
                onChange={value => handleTextChange(value, language, 'excerpt')}
                onUpdate={value => handleTextChange(value, language, 'excerpt')}
                placeholderText="Excerpt"
                value={promotion?.text?.[language]?.excerpt || ''}
                autocomplete={language === 'es_MX' ? 'off' : 'on'}
              />
              <TextInput
                feedbackType="info"
                inputSize="medium"
                label="Title*"
                className="promotion-text-input"
                onChange={value => handleTextChange(value, language, 'title')}
                onUpdate={value => handleTextChange(value, language, 'title')}
                placeholderText="Get 10% off when buying any 5 Lays or Doritos"
                value={promotion?.text?.[language]?.title || ''}
                autocomplete={language === 'es_MX' ? 'off' : 'on'}
              />
              <TextInput
                feedbackType="info"
                inputSize="medium"
                label="Content"
                className="promotion-text-input"
                onChange={value => handleTextChange(value, language, 'content')}
                onUpdate={value => handleTextChange(value, language, 'content')}
                placeholderText="Content"
                value={promotion?.text?.[language]?.content || ''}
                autocomplete={language === 'es_MX' ? 'off' : 'on'}
                maxLength={70}
              />
              <TextInput
                feedbackType="info"
                inputSize="medium"
                label="Image Text"
                className="promotion-text-input"
                onChange={value => handleTextChange(value, language, 'bannerText')}
                onUpdate={value => handleTextChange(value, language, 'bannerText')}
                placeholderText="Image Text"
                value={promotion?.text?.[language]?.bannerText || ''}
                autocomplete={language === 'es_MX' ? 'off' : 'on'}
                maxLength={100}
              />
              <TextArea
                label="Description"
                className="promotion-text-input"
                maxLength={500}
                updateValue={value => handleTextChange(value, language, 'description')}
                value={promotion?.text?.[language]?.description || ''}
                uiSchema={{
                  helperText: '',
                  placeholder: 'Type here',
                  showToolbar: false,
                  className: 'description',
                  showCharacterCount: true,
                }}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PromotionsDisplayConfig
